"use client";

import {
  isLocale,
  LOCALE_CHOICE_COOKIE,
  LOCALE_COOKIE,
  localizeHref,
  splitLocalePath,
  type Locale,
} from "./locales";

/**
 * Path with any locale prefix stripped.
 *
 * Delegates to `splitLocalePath` rather than keeping its own copy: the copy
 * matched only the NON-default prefixes, so it left `/en/` in place and
 * switching to Swedish from `/en/terms` navigated to `/sv/en/terms` — a 404.
 */
export const unprefixedPath = (path: string): string => splitLocalePath(path).path;

/**
 * Where a just-made switch is left for the reload to pick up.
 *
 * `sessionStorage`, not a cookie or a query param: it is per-tab, it survives
 * the navigation this function performs, it dies with the tab, and it never
 * reaches the server on its own — the one consumer reads it and sends it
 * deliberately.
 */
const SWITCH_FLAG = "sq_lang_switched";

/**
 * The locale the person just switched TO, if they did so since this was last
 * called. Reading it CLEARS it, so one switch produces one signal.
 *
 * Exists because switching is a *request* that survives a page load: the
 * onboarding conversation is mid-flight when someone changes the switcher, and
 * the reload that follows is the only chance to tell the agent about it. Every
 * other consumer of a locale wants the current value, which is what the cookie
 * and `<html lang>` are for — this is specifically "a change just happened".
 *
 * Clearing on read is what keeps it honest: a flag left lying around would
 * fire again on an unrelated conversation started later in the same tab.
 */
export const consumeLocaleSwitch = (): Locale | null => {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(SWITCH_FLAG);
    if (value === null) return null;
    window.sessionStorage.removeItem(SWITCH_FLAG);
    return isLocale(value) ? value : null;
  } catch {
    // Private mode / disabled storage — the switch still happened, we just
    // cannot tell anyone about it. Never worth throwing over.
    return null;
  }
};

/**
 * Switch the UI locale (shared by the public header menu and the profile-menu
 * toggle). Writes both locale cookies — what to render, and the fact that a
 * person chose it (locales.ts) — persists the choice for signed-in users
 * (runtime emails/notices follow users.ui_language — the keepalive beacon
 * survives the navigation; anonymous callers 401 harmlessly), then performs a
 * FULL navigation: the locale is fixed per page load by design
 * (docs/i18n-plan.md).
 *
 * WHERE IT NAVIGATES is `localizeHref`'s decision, not this function's. Only
 * MARKETING paths carry a locale prefix; app, auth and admin routes are
 * unprefixed by design and take their language from the cookie this just set.
 * Prefixing unconditionally is what sent a signed-in user switching to German
 * to `/de/a/<slug>` — a route that does not exist, so a 404 from their own
 * language picker. `localizeHref` already owns that rule and every
 * `LocalizedLink` uses it; this used to own half of it, wrongly.
 */
export const switchLocale = (locale: Locale, pathname: string): void => {
  const year = String(60 * 60 * 24 * 365);
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${year}; samesite=lax`;
  // This function is the ONLY writer of the choice cookie — it is the only
  // place a person picks a language rather than a browser reporting one. That
  // is what a later login persists to users.ui_language (see locales.ts).
  document.cookie = `${LOCALE_CHOICE_COOKIE}=${locale}; path=/; max-age=${year}; samesite=lax`;
  void fetch("/api/ui-language", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ locale }),
    keepalive: true,
  }).catch(() => undefined);

  // Leave a one-shot marker for whatever is mid-flight across the reload
  // below. Today that is the onboarding conversation, which needs to be told
  // to carry on in the new language; the opening language hint is read once,
  // at the arrival seed, and never again.
  try {
    window.sessionStorage.setItem(SWITCH_FLAG, locale);
  } catch {
    // Storage unavailable — the switch itself still works.
  }

  const target = localizeHref(unprefixedPath(pathname), locale);
  // On an app path the URL does not change, and `assign` to the current URL
  // would push a duplicate history entry — so Back would appear to do nothing.
  // The page still has to reload: the locale is fixed per page load.
  if (target === pathname) window.location.reload();
  else window.location.assign(target);
};
